import { ArrowDropDown } from '@mui/icons-material';
import { Autocomplete, AutocompleteRenderGetTagProps, Button, Chip, InputAdornment, SxProps, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { ChangeEvent, FocusEvent, HTMLAttributes, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import FileCirclePlusGray from '../../../assets/images/file-circle-plus-gray.svg';
import FileCirclePlus from '../../../assets/images/file-circle-plus.svg';
import { InputType, SetterFunction, ValidatorFunction, mapTypeToInputType, parseByType } from './types';

type SelectOption = {
  value: any;
  label: string;
  disabled?: boolean;
  element?: JSX.Element;
  nativeOptionHover?: boolean;
};

export type AutocompleteOptionsType = {
  readOnly?: boolean;
  options?: SelectOption[];
  clearable?: boolean;
};

export type TagsOptionsType = {
  readOnly?: boolean;
};

export type TextOptionsType = {
  trimOnBlur?: boolean;
};

export type InputOptionsType = {
  disabled?: boolean;
  autoFocus?: boolean;
  variant?: 'standard' | 'filled' | 'outlined';
  adornment?: ReactNode;
  help?: string;
  sx?: SxProps;
  label?: string;
  focusedLabel?: string;
  multiline?: boolean;
  showRequiredIndicator?: boolean;
  autoComplete?: string;
};

export type UseFormItemProps<T> = {
  type: InputType;
  label?: string;
  value: T;
  inputOptions?: InputOptionsType;
  validator: ValidatorFunction | null;
  textOptions?: TextOptionsType;
  autocompleteOptions?: AutocompleteOptionsType;
  tagsOptions?: TagsOptionsType;
};

export type UseFormItemHook<T> = [T, JSX.Element, boolean, string | null, (value: T) => void, (error: string | null) => void];

const FormStandardInput = (
  label: string | undefined,
  type: InputType,
  inputValue: any,
  publishChange: SetterFunction<any>,
  errorMessage: string | null,
  inputOptions: InputOptionsType = {},
  textOptions: TextOptionsType = {}
) => {
  const [focused, setFocused] = useState<boolean>(false);

  const {
    disabled,
    autoFocus,
    variant = 'standard',
    adornment,
    help: helpMessage = '',
    sx: inputSX = {},
    label: inputLabel,
    focusedLabel,
    multiline = false,
    showRequiredIndicator,
    autoComplete,
  } = inputOptions;
  const sx = { width: '100%', ...inputSX };
  const help = errorMessage === null ? helpMessage || null : errorMessage;
  const hasError = errorMessage !== null;

  // By default, `label` overrides `inputProperties.label`
  // If `inputProperties.focusedLabel` is provided, it overrides
  // the default label when the element is focused or it has a value
  // (i.e. when the text is displayed on the top-left corner of the input box)
  const blurLabel = label || inputLabel;
  const focusLabel = focusedLabel || blurLabel;
  const displayedLabel = focused || inputValue ? focusLabel : blurLabel;

  const onFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const onBlur = useCallback(
    ({ target: { value: eventValue } }: FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      if (textOptions.trimOnBlur) {
        publishChange(eventValue.trim());
      }
    },
    [publishChange, textOptions.trimOnBlur]
  );

  const onChange = useCallback(
    ({ target: { value: eventValue } }: ChangeEvent<HTMLInputElement>) => {
      publishChange(eventValue);
    },
    [publishChange]
  );

  return (
    <TextField
      type={mapTypeToInputType(type)}
      value={inputValue === null ? '' : inputValue}
      label={displayedLabel}
      variant={variant}
      autoFocus={autoFocus}
      disabled={disabled}
      error={hasError}
      sx={sx}
      multiline={multiline}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      helperText={help}
      required={showRequiredIndicator}
      autoComplete={autoComplete}
      InputProps={{
        endAdornment: adornment ? <InputAdornment position="end">{adornment}</InputAdornment> : null,
      }}
    />
  );
};

const FormTagsInput = (
  label: string | undefined,
  inputValue: string[],
  publishChange: SetterFunction<string[]>,
  errorMessage: string | null,
  inputOptions: InputOptionsType = {},
  tagsOptions: TagsOptionsType = {}
) => {
  const [focused, setFocused] = useState<boolean>(false);
  const [value, setValue] = useState<string[]>(inputValue);

  const { sx: sxProp = {}, help: helpProp, label: labelProp, focusedLabel, disabled, variant = 'standard', showRequiredIndicator, autoFocus } = inputOptions;

  const sx = { width: '100%', ...sxProp };
  const help = errorMessage === null ? helpProp || null : errorMessage;
  const hasError = errorMessage !== null;
  const { readOnly = false } = tagsOptions;

  const displayedLabel = useMemo(() => {
    // By default, `label` overrides `inputProperties.label`
    // If `inputProperties.focusedLabel` is provided, it overrides
    // the default label when the element is focused or it has a value
    // (i.e. when the text is displayed on the top-left corner of the input box)
    const blurLabel = label || labelProp;
    const focusLabel = focusedLabel || blurLabel;
    const hasValue = inputValue?.length > 0;
    return focused || hasValue ? focusLabel : blurLabel;
  }, [label, focusedLabel, labelProp, focused, inputValue]);

  const renderTags = useCallback((values: string[], getTagProps: AutocompleteRenderGetTagProps) => {
    return values.map((option: string, index: number) => {
      const { key, ...rest } = getTagProps({ index });
      return <Chip key={key} color="primary" variant="filled" size="small" label={option} {...rest} />;
    });
  }, []);

  const onChange = useCallback(
    (_: unknown, values: string[]) => {
      const trimmed = values.map((v) => v.trim()).filter((v) => !!v);
      setValue(trimmed);
      publishChange(trimmed);
    },
    [publishChange]
  );

  return (
    <Autocomplete
      options={[]}
      disabled={disabled}
      sx={sx}
      value={value}
      onChange={onChange}
      autoSelect={true}
      freeSolo={true}
      multiple={true}
      renderTags={renderTags}
      renderInput={({ inputProps, ...rest }) => (
        <TextField
          {...rest}
          inputProps={{ ...inputProps, readOnly, 'aria-controls': inputProps['aria-controls'] ?? '' }}
          variant={variant}
          label={displayedLabel}
          helperText={help}
          error={hasError}
          required={showRequiredIndicator}
          autoFocus={autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      )}
    />
  );
};

const FormAutocomplete = (
  label: string | undefined,
  value: any,
  publishChange: SetterFunction<SelectOption | null>,
  errorMessage: string | null,
  inputOptions: InputOptionsType = {},
  autocompleteOptions: AutocompleteOptionsType = {}
) => {
  const [focused, setFocused] = useState<boolean>(false);
  const sx = { width: '100%', ...(inputOptions.sx || {}) };
  const help = errorMessage === null ? inputOptions.help || null : errorMessage;
  const hasError = errorMessage !== null;
  const { options = [], readOnly = false, clearable = true } = autocompleteOptions;

  const displayedLabel = useMemo(() => {
    // By default, `label` overrides `inputProperties.label`
    // If `inputProperties.focusedLabel` is provided, it overrides
    // the default label when the element is focused or it has a value
    // (i.e. when the text is displayed on the top-left corner of the input box)
    const blurLabel = label || inputOptions.label;
    const focusLabel = inputOptions.focusedLabel || blurLabel;
    const hasValue = !!value;
    return focused || hasValue ? focusLabel : blurLabel;
  }, [label, inputOptions.focusedLabel, inputOptions.label, focused, value]);

  const defaultValue = useMemo(() => options.find((o) => o.value === value) || null, [value, options]);

  const renderOption = useMemo(() => {
    return (props: HTMLAttributes<HTMLLIElement>, option: any) => {
      const itemProps = { ...props };
      if (option.element) {
        const itemStyle = itemProps.style || {};
        const optionStyle = option.nativeOptionHover ? {} : { backgroundColor: 'white' };
        itemProps.style = { ...itemStyle, ...optionStyle };
      }
      return (
        <li {...itemProps} key={option.value}>
          {option.element || option.label}
        </li>
      );
    };
  }, []);

  return (
    <Autocomplete
      options={options}
      disabled={inputOptions.disabled}
      sx={sx}
      value={defaultValue}
      disableClearable={!clearable}
      onChange={(_, option) => publishChange(option)}
      getOptionLabel={(o) => o.label}
      getOptionDisabled={(o) => !!o.disabled}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      popupIcon={inputOptions.adornment || <ArrowDropDown />}
      renderOption={renderOption}
      renderInput={({ inputProps, ...rest }) => (
        <TextField
          {...rest}
          inputProps={{ ...inputProps, readOnly, 'aria-controls': inputProps['aria-controls'] ?? '' }}
          variant={inputOptions.variant ?? 'standard'}
          label={displayedLabel}
          helperText={help}
          error={hasError}
          required={inputOptions.showRequiredIndicator}
          autoComplete={inputOptions.autoComplete}
          autoFocus={inputOptions.autoFocus}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      )}
    />
  );
};

const FormFileDragDrop = (label: string | undefined, publishChange: SetterFunction<File | null>) => {
  const [active, setActive] = useState<boolean>(false);
  const [valid, setValid] = useState<boolean>(true);

  const onDragEnter: React.DragEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setActive(true);
    setValid(event.dataTransfer.items.length === 1);
  };

  const onDragOver: React.DragEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave: React.DragEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();
    setActive(false);
    setValid(true);
  };

  const onDrop: React.DragEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();
    setActive(false);
    setValid(true);
    const files = [...event.dataTransfer.files];
    if (files.length === 1) {
      publishChange(files[0]);
    }
  };

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const files = [...(event.target.files || [])];
    if (files.length === 1) {
      publishChange(files[0]);
    }
  };

  return (
    <Button
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      component="label"
      role={undefined}
      sx={{
        py: 6,
        px: 0,
        border: `2px dashed`,
        borderRadius: '4px',
        borderColor: active && valid ? 'primary.main' : 'text.disabled',
        textTransform: 'none',
        width: 1,
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      {active && valid && <FileCirclePlus aria-hidden="true" height="70" width="80" viewBox="0 0 80 70" fill="white" />}
      {!(active && valid) && <FileCirclePlusGray aria-hidden="true" height="70" width="80" viewBox="0 0 80 70" fill="white" />}
      <Typography sx={{ mt: 2 }}>{t('dragDrop')}</Typography>
      {label && <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 1, mb: 0 }}>{label}</Typography>}
      <input
        type="file"
        autoFocus
        onChange={onInputChange}
        style={{
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          height: 1,
          overflow: 'hidden',
          position: 'absolute',
          bottom: 0,
          left: 0,
          whiteSpace: 'nowrap',
          width: 1,
        }}
      />
    </Button>
  );
};

export const useFormItem = function <T = unknown>(props: UseFormItemProps<T>): UseFormItemHook<T> {
  const { type, label, value, validator = null, inputOptions = {}, textOptions = {}, tagsOptions = {}, autocompleteOptions = {} } = props;
  const [initialized, setInitialized] = useState<boolean>(false);

  // Input value and validity need to be updated together: a sequential update
  // would cause unexpected behavior in case both the value and the error
  // are used as dependencies for `useMemo` or `useEffect`
  // (e.g. an invalid input could be processed as valid before the error is set)
  const [inputState, setInputState] = useState<{ value: any; valid: boolean; error: string | null }>({
    value: value,
    valid: true,
    error: null,
  });

  // If the provided value is not a primitive type, the below useEffect will
  // trigger indefinitely because the references of the dependency are different.
  // Stringify the input value so the update is run only if it has really changed
  const depValue = JSON.stringify(value);

  const validate = useCallback(
    (newValue: any) => {
      if (validator === null) {
        setInputState({ value: newValue, valid: true, error: null });
        return;
      }

      const validatorValue = validator(newValue);

      // If valid
      if (validatorValue === true) {
        setInputState({ value: newValue, valid: true, error: null });
        return;
      }

      setInputState({ value: newValue, valid: false, error: initialized ? validatorValue : null });
    },
    // TODO: Think of a better way of handling validators. They are breaking React hooks as
    // they return a new function instance on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialized]
  );

  useEffect(() => {
    validate(value);

    // The default value has been processed, validate future updates normally
    setInitialized(true);
    // TODO: Think of a better way of handling this initial validation. This is also
    // breaking React hooks as it would re-run on every value change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depValue]);

  const handleSetValue = useCallback(<T,>(value: T) => validate(value), [validate]);

  const handleSetError = useCallback((error: string | null) => setInputState((s) => ({ ...s, error })), []);

  const onAutocompleteChange = useCallback((option: SelectOption | null) => handleSetValue(option?.value || null), [handleSetValue]);

  const onTagsChange = useCallback((tags: string[]) => handleSetValue(tags), [handleSetValue]);

  const onFileChange = useCallback((file: File | null) => handleSetValue(file), [handleSetValue]);

  const onInputChange = useCallback((value: any) => handleSetValue(parseByType(type, value)), [handleSetValue, type]);

  let component;

  switch (type) {
    case InputType.TYPE_AUTOCOMPLETE:
      component = FormAutocomplete(label, inputState.value, onAutocompleteChange, inputState.error, inputOptions, autocompleteOptions);
      break;
    case InputType.TYPE_FREE_TAGS:
      component = FormTagsInput(label, inputState.value, onTagsChange, inputState.error, inputOptions, tagsOptions);
      break;
    case InputType.TYPE_FILE_DRAG_DROP:
      component = FormFileDragDrop(label, onFileChange);
      break;
    default:
      component = FormStandardInput(label, type, inputState.value, onInputChange, inputState.error, inputOptions, textOptions);
      break;
  }

  return [inputState.value, component, inputState.valid, inputState.error, handleSetValue, handleSetError];
};
